import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "../../config/Api";

export interface DealState {
  deals: any[];
  loading: boolean;
  error: string | null;
}

const initialState: DealState = {
  deals: [],
  loading: false,
  error: null,
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("jwt");
  return token ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` } : {};
};

// ================= THUNKS =================

export const fetchDeals = createAsyncThunk(
  "deals/fetchDeals",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/products/deals");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch deals");
    }
  }
);

export const createDeal = createAsyncThunk(
  "deals/createDeal",
  async (dealData: any, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/admin/deals", dealData, { headers: getAuthHeaders() });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to create deal");
    }
  }
);

export const updateDeal = createAsyncThunk(
  "deals/updateDeal",
  async ({ id, dealData }: { id: number; dealData: any }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/api/admin/deals/${id}`, dealData, { headers: getAuthHeaders() });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to update deal");
    }
  }
);

export const addProductToDeal = createAsyncThunk(
  "deals/addProductToDeal",
  async ({ dealId, productId }: { dealId: number; productId: number }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/api/admin/deals/${dealId}/products/${productId}`,
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to add product to deal");
    }
  }
);

export const removeProductFromDeal = createAsyncThunk(
  "deals/removeProductFromDeal",
  async ({ dealId, productId }: { dealId: number; productId: number }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `/api/admin/deals/${dealId}/products/${productId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to remove product from deal");
    }
  }
);

export const deleteDeal = createAsyncThunk(
  "deals/deleteDeal",
  async (dealId: number, { rejectWithValue }) => {
    try {
      await api.delete(`/api/admin/deals/${dealId}`, { headers: getAuthHeaders() });
      return dealId;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed to delete deal");
    }
  }
);

// ================= SLICE =================

const dealSlice = createSlice({
  name: "deal",
  initialState,
  reducers: {
    clearDealError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Deals
      .addCase(fetchDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        state.loading = false;
        state.deals = action.payload;
      })
      .addCase(fetchDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create Deal
      .addCase(createDeal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDeal.fulfilled, (state, action) => {
        state.loading = false;
        state.deals.push(action.payload);
      })
      .addCase(createDeal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Deal
      .addCase(updateDeal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.deals.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deals[index] = action.payload;
        }
      })

      // Add Product to Deal
      .addCase(addProductToDeal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.deals.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deals[index] = action.payload;
        }
      })

      // Remove Product from Deal
      .addCase(removeProductFromDeal.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.deals.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deals[index] = action.payload;
        }
      })

      // Delete Deal
      .addCase(deleteDeal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDeal.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.deals = state.deals.filter((d) => d.id !== action.payload);
      })
      .addCase(deleteDeal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDealError } = dealSlice.actions;
export default dealSlice.reducer;